/* The seven dimensions, in the order every surface renders them. Pure.
 *
 * **Fixed, and never sorted by value.** `orderedDimensions` sorts by weight for the decomposition,
 * where the question is "which of these counts for us"; a *strip* answers a different question —
 * "is consistency red across the estate" — and that only works if the fourth cell is consistency on
 * every row. Two orders, each right for its own reading, and both declared rather than incidental.
 *
 * `accuracy` sits third and is expected to be empty on every score: no rule kind can express
 * "matches reality", which needs a reference source. It is listed rather than hidden, because
 * `unmeasurable_dimensions` exists precisely so a reader can tell the platform's gap from their own.
 */

import type { Dimension, DimensionScore, Score } from '../api/scoring';

import { absenceKey, absenceReason } from './presentation';

export const DIMENSION_ORDER: readonly Dimension[] = [
  'completeness',
  'validity',
  'accuracy',
  'consistency',
  'uniqueness',
  'timeliness',
  'integrity',
];

/**
 * The i18n key explaining why a dimension has no value.
 *
 * A dimension missing from the payload entirely resolves to "no evidence" rather than throwing: the
 * contract says all seven are always present, and a client that crashed when a server sent six
 * would turn a contract drift into a blank screen. The honest fallback is the least specific of the
 * four reasons, never the most reassuring one.
 */
export function absence(entry: DimensionScore | undefined, score: Score): string {
  if (entry === undefined) return absenceKey('no-evidence');
  return absenceKey(absenceReason(entry, score.unmeasurable_dimensions) ?? 'no-evidence');
}
